import React, { useState, useEffect, useContext, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import whatsappIcon from '../../assets/nopicture.png'
import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";
import { AuthContext } from "../../context/Auth/AuthContext";
import useWhatsApps from "../../hooks/useWhatsApps";

import { Can } from "../Can";
import { Avatar, Grid, Input, Paper, Tab, Tabs, Typography, Box, Chip, Checkbox, ListItemText, Switch, FormControlLabel, Tooltip, IconButton } from "@material-ui/core";
import InfoIcon from '@material-ui/icons/Info';
import { getBackendUrl } from "../../config";
import TabPanel from "../TabPanel";
import AvatarUploader from "../AvatarUpload";

const backendUrl = getBackendUrl();
const path = require('path');

const useStyles = makeStyles(theme => ({
    root: {
        display: "flex",
        flexWrap: "wrap",
    },
    multFieldLine: {
        display: "flex",
        "& > *:not(:last-child)": {
            marginRight: theme.spacing(1),
        },
    },
    btnWrapper: {
        position: "relative",
    },
    buttonProgress: {
        color: green[500],
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },
    formControl: {
        margin: theme.spacing(1),
        minWidth: 120,
    },
    textField: {
        marginRight: theme.spacing(1),
        flex: 1,
    },
    container: {
        display: 'flex',
        flexWrap: 'wrap',
    },
    avatar: {
        width: theme.spacing(12),
        height: theme.spacing(12),
        margin: theme.spacing(2),
        cursor: 'pointer',
        borderRadius: '50%',
        border: '2px solid #ccc',
    },
    updateDiv: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
    },
    updateInput: {
        display: 'none',
    },
    updateLabel: {
        padding: theme.spacing(1),
        margin: theme.spacing(1),
        textTransform: 'uppercase',
        textAlign: 'center',
        cursor: 'pointer',
        border: '2px solid #ccc',
        borderRadius: '5px',
        minWidth: 160,
        fontWeight: 'bold',
        color: '#555',
    },
    errorUpdate: {
        border: '2px solid red',
    },
    errorText: {
        color: 'red',
        fontSize: '0.8rem',
        fontWeight: 'bold',
    }
}));

const UserSchema = Yup.object().shape({
    name: Yup.string()
        .min(2, "Muito curto!")
        .max(50, "Muito longo!")
        .required("Obrigatório"),
    password: Yup.string()
        .min(5, "Muito curto!")
        .max(50, "Muito longo!")
        .nullable(),
    email: Yup.string()
        .email("Email inválido")
        .matches(/^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i, "É obrigatório cadastrar um e-mail válido.")
        .test('no-uppercase', 'Não é permitido letras maiúsculas no e-mail.', value => !/[A-Z]/.test(value || ''))
        .required("Obrigatório"),
    allHistoric: Yup.string().nullable(),
    allowConnections: Yup.string().nullable(),
    allowCampaigns: Yup.string().nullable(),
    allowContactSources: Yup.string().nullable(),
    allowBulkTicketActions: Yup.string().nullable(),
    allowColumnManagement: Yup.string().nullable(),
    allowViewOtherConnections: Yup.string().nullable(),
    allowContactImportExport: Yup.string().nullable(),
});

const UserModal = ({ open, onClose, userId }) => {
    const classes = useStyles();

    const initialState = {
        name: "",
        email: "",
        password: "",
        profile: "user",
        startWork: "00:00",
        endWork: "23:59",
        farewellMessage: "",
        allTicket: "disable",
        allowGroup: false,
        defaultTheme: "light",
        defaultMenu: "open",
        allHistoric: "disabled",
        allUserChat: "disabled",
        userClosePendingTicket: "enabled",
        showDashboard: "disabled",
        allowRealTime: "disabled",
        allowConnections: "disabled",
        allowCampaigns: "disabled",
            allowContactSources: "disabled",
    allowBulkTicketActions: "disabled",
    allowColumnManagement: "disabled",
    allowViewOtherConnections: "disabled",
    allowContactImportExport: "disabled"
    };

    const { user: loggedInUser } = useContext(AuthContext);
    const isAdmin = loggedInUser.profile === 'admin'; // Verifica se o usuário é admin

    const [user, setUser] = useState(initialState);
    const [selectedQueueIds, setSelectedQueueIds] = useState([]);
    const [whatsappId, setWhatsappId] = useState("");
    const { loading, whatsApps } = useWhatsApps();
    const [profileUrl, setProfileUrl] = useState(null)
    const [tab, setTab] = useState("general");
    const [avatar, setAvatar] = useState(null);
    const startWorkRef = useRef();
    const endWorkRef = useRef();

    useEffect(() => {
        const fetchUser = async () => {
            if (!userId) return;
            try {
                const { data } = await api.get(`/users/${userId}`);
                console.log("Dados recebidos do backend:", data);
                console.log("allowViewOtherConnections recebido:", data.allowViewOtherConnections);
                setUser(prevState => ({
                    ...prevState,
                    ...data,
                    allowViewOtherConnections: data.allowViewOtherConnections || "disabled"
                }));

                const { profileImage } = data;
                setProfileUrl(`${backendUrl}/public/company${data.companyId}/user/${profileImage}`);

                const userQueueIds = data.queues?.map(queue => queue.id);
                setSelectedQueueIds(userQueueIds);
                setWhatsappId(data.whatsappId ? data.whatsappId : '');
            } catch (err) {
                toastError(err);
            }
        };

        fetchUser();
    }, [userId, open]);

    const handleClose = () => {
        onClose();
        setUser(initialState);
        setSelectedQueueIds([]);
        setWhatsappId("");
        setProfileUrl(null);
        setTab("general");
        setAvatar(null);
    };

    const handleTabChange = (event, newValue) => {
        setTab(newValue);
    };

    const handleSaveUser = async (values) => {
        const uploadAvatar = async (file, userId) => {
            const formData = new FormData();
            formData.append('userId', userId);
            formData.append('typeArch', "user");
            formData.append('profileImage', avatar);

            try {
                const { data } = await api.post(`/users/${userId}/media-upload`, formData);
                // Atualiza o localStorage ou o estado com a nova imagem
                localStorage.setItem("profileImage", data.user.profileImage);
                setProfileUrl(`${backendUrl}/public/company${data.user.companyId}/user/${data.user.profileImage}`);
            } catch (err) {
                toastError(err);
            }
        };

        const userData = {
            ...values,
            whatsappId: whatsappId || null, // Converter string vazia para null
            queueIds: selectedQueueIds,
            allowViewOtherConnections: values.allowViewOtherConnections,
            allowContactImportExport: values.allowContactImportExport
        };

        console.log("Dados sendo enviados:", userData);
        console.log("allowViewOtherConnections value:", values.allowViewOtherConnections);

        // Se estamos editando e a senha está vazia, removê-la do userData
        if (userId && (!userData.password || userData.password.trim() === '')) {
            delete userData.password;
        }

        try {
            let newUser;
            if (userId) {
                // Atualizando um usuário existente
                console.log("Atualizando usuário com dados:", userData);
                const { data } = await api.put(`/users/${userId}`, userData);
                console.log("Resposta da atualização:", data);
                newUser = data;
            } else {
                // Criando um novo usuário
                const { data } = await api.post("/users", userData);
                newUser = data;
            }

            // Se o avatar foi definido, faça o upload
            if (avatar) {
                await uploadAvatar(newUser, newUser.id); // Passa o ID do usuário criado/atualizado
            }

            // Fechar modal e mostrar sucesso
            handleClose();
            toast.success(i18n.t("userModal.success"));
        } catch (err) {
            toastError(err);
        }
    };

    return (
        <div className={classes.root}>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
                scroll="paper"
            >
                <DialogTitle id="form-dialog-title">
                    {userId
                        ? `${i18n.t("userModal.title.edit")}`
                        : `${i18n.t("userModal.title.add")}`}
                </DialogTitle>
                <Formik
                    initialValues={user}
                    enableReinitialize={true}
                    validationSchema={UserSchema}

                    validate={values => {
                        const errors = {};
                        if (values.email && /[A-Z]/.test(values.email)) {
                            errors.email = "Não é permitido letras maiúsculas no e-mail.";
                        }
                        // Se estamos editando e a senha está vazia, não é obrigatória
                        if (!userId && (!values.password || values.password.trim() === '')) {
                            errors.password = "Obrigatório";
                        }
                        return errors;
                    }}
                    onSubmit={(values, actions) => {
                        setTimeout(() => {
                            handleSaveUser(values);
                            actions.setSubmitting(false);
                        }, 400);
                    }}
                >
                    {({ touched, errors, isSubmitting, setFieldValue, values, isValid }) => (
                        <Form>
                            <Paper className={classes.mainPaper} elevation={1}>
                                <Tabs
                                    value={tab}
                                    indicatorColor="primary"
                                    textColor="primary"
                                    scrollButtons="on"
                                    variant="scrollable"
                                    onChange={handleTabChange}
                                    className={classes.tab}
                                >
                                    <Tab label={i18n.t("userModal.tabs.general")} value={"general"} />
                                    <Tab label={i18n.t("userModal.tabs.permissions")} value={"permissions"} />
                                </Tabs>
                            </Paper>
                            <Paper className={classes.paper} elevation={0}>
                                <DialogContent dividers>
                                    <TabPanel
                                        className={classes.container}
                                        value={tab}
                                        name={"general"}
                                    >
                                        <Grid
                                            container
                                            spacing={1}
                                            alignContent="center"
                                            alignItems="center"
                                            justifyContent="center">
                                            <FormControl className={classes.updateDiv}>
                                                <AvatarUploader
                                                    setAvatar={setAvatar}
                                                    avatar={user.profileImage}
                                                    companyId={user.companyId}
                                                />
                                                {user.profileImage &&
                                                    <Button
                                                        variant="outlined"
                                                        color="secondary"
                                                        onClick={() => {
                                                            user.profileImage = null;
                                                            setFieldValue("profileImage", null);
                                                            setAvatar(null);
                                                        }}
                                                    >
                                                        {i18n.t("userModal.title.removeImage")}
                                                    </Button>
                                                }
                                            </FormControl>
                                        </Grid>
                                        <Grid container spacing={1}>
                                            <Grid item xs={12} md={6} xl={6}>
                                                <Field
                                                    as={TextField}
                                                    label={i18n.t("userModal.form.name")}
                                                    autoFocus
                                                    name="name"
                                                    error={touched.name && Boolean(errors.name)}
                                                    helperText={touched.name && errors.name}
                                                    variant="outlined"
                                                    margin="dense"
                                                    fullWidth
                                                    disabled={!isAdmin} // Desabilita se não for admin
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6} xl={6}>
                                                <Field
                                                    as={TextField}
                                                    label={userId ? "Nova senha (opcional)" : i18n.t("userModal.form.password")}
                                                    type="password"
                                                    name="password"
                                                    error={touched.password && Boolean(errors.password)}
                                                    helperText={touched.password && errors.password}
                                                    variant="outlined"
                                                    margin="dense"
                                                    fullWidth
                                                    onChange={e => {
                                                        setFieldValue("password", e.target.value);
                                                    }}
                                                    value={values.password}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid container spacing={1}>
                                            <Grid item xs={12} md={8} xl={8}>
                                                <Field
                                                    as={TextField}
                                                    label={i18n.t("userModal.form.email")}
                                                    name="email"
                                                    error={touched.email && Boolean(errors.email)}
                                                    helperText={
                                                        (touched.email && errors.email) ? errors.email :
                                                        (touched.email && !values.email) ? "é obrigatório cadastrar um e-mail válido." : ""
                                                    }
                                                    variant="outlined"
                                                    margin="dense"
                                                    fullWidth
                                                    onChange={e => {
                                                        const lower = e.target.value.toLowerCase();
                                                        setFieldValue("email", lower);
                                                    }}
                                                    value={values.email}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={4} xl={4}>
                                                <FormControl
                                                    variant="outlined"
                                                    margin="dense"
                                                    fullWidth
                                                >
                                                    {isAdmin && ( // Apenas admins podem ver este campo
                                                        <>
                                                            <InputLabel id="profile-selection-input-label">
                                                                {i18n.t("userModal.form.profile")}
                                                            </InputLabel>

                                                            <Field
                                                                as={Select}
                                                                label={i18n.t("userModal.form.profile")}
                                                                name="profile"
                                                                labelId="profile-selection-label"
                                                                id="profile-selection"
                                                                required
                                                            >
                                                                <MenuItem value="admin">Admin</MenuItem>
                                                                <MenuItem value="user">User</MenuItem>
                                                            </Field>
                                                        </>
                                                    )}
                                                </FormControl>
                                            </Grid>
                                        </Grid>
                                        <Grid container spacing={1}>
                                            <Grid item xs={12} md={12} xl={12}>
                                                {isAdmin && ( // Apenas admins podem ver este campo
                                                    <QueueSelect
                                                        selectedQueueIds={selectedQueueIds}
                                                        onChange={values => setSelectedQueueIds(values)}
                                                        fullWidth
                                                    />
                                                )}
                                            </Grid>
                                        </Grid>
                                        <Grid container spacing={1}>
                                            <Grid item xs={12} md={12} xl={12}>
                                                {isAdmin && ( // Apenas admins podem ver este campo
                                                    <FormControl variant="outlined" margin="dense" className={classes.maxWidth} fullWidth>
                                                        <InputLabel>
                                                            {i18n.t("userModal.form.whatsapp")}
                                                        </InputLabel>
                                                        <Select
                                                            value={whatsappId}
                                                            onChange={(e) => setWhatsappId(e.target.value)}
                                                            label={i18n.t("userModal.form.whatsapp")}
                                                        >
                                                            <MenuItem value="">
                                                                <em>{i18n.t("userModal.form.whatsapp")}</em>
                                                            </MenuItem>
                                                            {whatsApps.map((whatsapp) => (
                                                                <MenuItem key={whatsapp.id} value={whatsapp.id}>
                                                                    {whatsapp.name}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                )}
                                            </Grid>
                                        </Grid>
                                        {isAdmin && ( // Apenas admins podem ver estes campos
                                            <>
                                                <Grid container spacing={1}>
                                                    <Grid item xs={12} md={6} xl={6}>
                                                        <Field
                                                            as={TextField}
                                                            label={i18n.t("userModal.form.startWork")}
                                                            type="time"
                                                            ampm={false}
                                                            inputRef={startWorkRef}
                                                            InputLabelProps={{
                                                                shrink: true,
                                                            }}
                                                            inputProps={{
                                                                step: 600, // 5 min
                                                            }}
                                                            fullWidth
                                                            name="startWork"
                                                            error={
                                                                touched.startWork && Boolean(errors.startWork)
                                                            }
                                                            helperText={
                                                                touched.startWork && errors.startWork
                                                            }
                                                            variant="outlined"
                                                            margin="dense"
                                                            className={classes.textField}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} md={6} xl={6}>
                                                        <Field
                                                            as={TextField}
                                                            label={i18n.t("userModal.form.endWork")}
                                                            type="time"
                                                            ampm={false}
                                                            inputRef={endWorkRef}
                                                            InputLabelProps={{
                                                                shrink: true,
                                                            }}
                                                            inputProps={{
                                                                step: 600, // 5 min
                                                            }}
                                                            fullWidth
                                                            name="endWork"
                                                            error={
                                                                touched.endWork && Boolean(errors.endWork)
                                                            }
                                                            helperText={
                                                                touched.endWork && errors.endWork
                                                            }
                                                            variant="outlined"
                                                            margin="dense"
                                                            className={classes.textField}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </>
                                        )}

                                        <Field
                                            as={TextField}
                                            label={i18n.t("userModal.form.farewellMessage")}
                                            type="farewellMessage"
                                            multiline
                                            rows={4}
                                            fullWidth
                                            name="farewellMessage"
                                            error={touched.farewellMessage && Boolean(errors.farewellMessage)}
                                            helperText={touched.farewellMessage && errors.farewellMessage}
                                            variant="outlined"
                                            margin="dense"
                                        />

                                        <Grid container spacing={1}>
                                            <Grid item xs={12} md={6} xl={6}>
                                                <FormControl
                                                    variant="outlined"
                                                    className={classes.maxWidth}
                                                    margin="dense"
                                                    fullWidth
                                                >
                                                    <InputLabel>
                                                        {i18n.t("userModal.form.defaultTheme")}
                                                    </InputLabel>

                                                    <Field
                                                        as={Select}
                                                        label={i18n.t("userModal.form.defaultTheme")}
                                                        name="defaultTheme"
                                                        type="defaultTheme"
                                                        required
                                                    >
                                                        <MenuItem value="light">{i18n.t("userModal.form.defaultThemeLight")}</MenuItem>
                                                        <MenuItem value="dark">{i18n.t("userModal.form.defaultThemeDark")}</MenuItem>
                                                    </Field>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12} md={6} xl={6}>

                                                <FormControl
                                                    variant="outlined"
                                                    className={classes.maxWidth}
                                                    margin="dense"
                                                    fullWidth
                                                >
                                                    <InputLabel>
                                                        {i18n.t("userModal.form.defaultMenu")}
                                                    </InputLabel>

                                                    <Field
                                                        as={Select}
                                                        label={i18n.t("userModal.form.defaultMenu")}
                                                        name="defaultMenu"
                                                        type="defaultMenu"
                                                        required
                                                    >
                                                        <MenuItem value={"open"}>{i18n.t("userModal.form.defaultMenuOpen")}</MenuItem>
                                                        <MenuItem value={"closed"}>{i18n.t("userModal.form.defaultMenuClosed")}</MenuItem>
                                                    </Field>
                                                </FormControl>
                                            </Grid>
                                        </Grid>
                                    </TabPanel>
                                    <TabPanel
                                        className={classes.container}
                                        value={tab}
                                        name={"permissions"}
                                    >
                                                                                 {isAdmin ? (
                                             // Renderiza os campos de permissões se for admin
                                             <>
                                                 {/* Primeira linha - 2 permissões */}
                                                 <Grid container spacing={2}>
                                                     <Grid item xs={12} md={6} xl={6}>
                                                         <FormControlLabel
                                                             control={
                                                                 <Field
                                                                     as={Switch}
                                                                     name="allTicket"
                                                                     checked={values.allTicket === "enable"}
                                                                     onChange={(e) => {
                                                                         setFieldValue("allTicket", e.target.checked ? "enable" : "disable");
                                                                     }}
                                                                     color="primary"
                                                                 />
                                                             }
                                                             label={
                                                                 <Box display="flex" alignItems="center">
                                                                     <Typography variant="body2">
                                                                         {i18n.t("userModal.form.allTicket")}
                                                                     </Typography>
                                                                 </Box>
                                                             }
                                                         />
                                                     </Grid>
                                                     <Grid item xs={12} md={6} xl={6}>
                                                         <FormControlLabel
                                                             control={
                                                                 <Field
                                                                     as={Switch}
                                                                     name="allowGroup"
                                                                     checked={values.allowGroup === true}
                                                                     onChange={(e) => {
                                                                         setFieldValue("allowGroup", e.target.checked);
                                                                     }}
                                                                     color="primary"
                                                                 />
                                                             }
                                                             label={
                                                                 <Box display="flex" alignItems="center">
                                                                     <Typography variant="body2">
                                                                         {i18n.t("userModal.form.allowGroup")}
                                                                     </Typography>
                                                                 </Box>
                                                             }
                                                         />
                                                     </Grid>
                                                 </Grid>

                                                 {/* Segunda linha - 2 permissões */}
                                                 <Grid container spacing={2}>
                                                     <Grid item xs={12} md={6} xl={6}>
                                                         <FormControlLabel
                                                             control={
                                                                 <Field
                                                                     as={Switch}
                                                                     name="allHistoric"
                                                                     checked={values.allHistoric === "enabled"}
                                                                     onChange={(e) => {
                                                                         setFieldValue("allHistoric", e.target.checked ? "enabled" : "disabled");
                                                                     }}
                                                                     color="primary"
                                                                 />
                                                             }
                                                             label={
                                                                 <Box display="flex" alignItems="center">
                                                                     <Typography variant="body2">
                                                                         {i18n.t("userModal.form.allHistoric")}
                                                                     </Typography>
                                                                 </Box>
                                                             }
                                                         />
                                                     </Grid>
                                                     <Grid item xs={12} md={6} xl={6}>
                                                         <FormControlLabel
                                                             control={
                                                                 <Field
                                                                     as={Switch}
                                                                     name="allUserChat"
                                                                     checked={values.allUserChat === "enabled"}
                                                                     onChange={(e) => {
                                                                         setFieldValue("allUserChat", e.target.checked ? "enabled" : "disabled");
                                                                     }}
                                                                     color="primary"
                                                                 />
                                                             }
                                                             label={
                                                                 <Box display="flex" alignItems="center">
                                                                     <Typography variant="body2">
                                                                         {i18n.t("userModal.form.allUserChat")}
                                                                     </Typography>
                                                                 </Box>
                                                             }
                                                         />
                                                     </Grid>
                                                 </Grid>

                                                 {/* Terceira linha - 2 permissões */}
                                                 <Grid container spacing={2}>
                                                     <Grid item xs={12} md={6} xl={6}>
                                                         <FormControlLabel
                                                             control={
                                                                 <Field
                                                                     as={Switch}
                                                                     name="userClosePendingTicket"
                                                                     checked={values.userClosePendingTicket === "enabled"}
                                                                     onChange={(e) => {
                                                                         setFieldValue("userClosePendingTicket", e.target.checked ? "enabled" : "disabled");
                                                                     }}
                                                                     color="primary"
                                                                 />
                                                             }
                                                             label={
                                                                 <Box display="flex" alignItems="center">
                                                                     <Typography variant="body2">
                                                                         {i18n.t("userModal.form.userClosePendingTicket")}
                                                                     </Typography>
                                                                 </Box>
                                                             }
                                                         />
                                                     </Grid>
                                                     <Grid item xs={12} md={6} xl={6}>
                                                         <FormControlLabel
                                                             control={
                                                                 <Field
                                                                     as={Switch}
                                                                     name="allowConnections"
                                                                     checked={values.allowConnections === "enabled"}
                                                                     onChange={(e) => {
                                                                         setFieldValue("allowConnections", e.target.checked ? "enabled" : "disabled");
                                                                     }}
                                                                     color="primary"
                                                                 />
                                                             }
                                                             label={
                                                                 <Box display="flex" alignItems="center">
                                                                     <Typography variant="body2">
                                                                         {i18n.t("userModal.form.allowConnections")}
                                                                     </Typography>
                                                                 </Box>
                                                             }
                                                         />
                                                     </Grid>
                                                 </Grid>

                                                 {/* Quarta linha - 2 permissões */}
                                                 <Grid container spacing={2}>
                                                     <Grid item xs={12} md={6} xl={6}>
                                                         <FormControlLabel
                                                             control={
                                                                 <Field
                                                                     as={Switch}
                                                                     name="allowRealTime"
                                                                     checked={values.allowRealTime === "enabled"}
                                                                     onChange={(e) => {
                                                                         setFieldValue("allowRealTime", e.target.checked ? "enabled" : "disabled");
                                                                     }}
                                                                     color="primary"
                                                                 />
                                                             }
                                                             label={
                                                                 <Box display="flex" alignItems="center">
                                                                     <Typography variant="body2">
                                                                         {i18n.t("userModal.form.allowRealTime")}
                                                                     </Typography>
                                                                 </Box>
                                                             }
                                                         />
                                                     </Grid>
                                                     <Grid item xs={12} md={6} xl={6}>
                                                         <FormControlLabel
                                                             control={
                                                                 <Field
                                                                     as={Switch}
                                                                     name="allowCampaigns"
                                                                     checked={values.allowCampaigns === "enabled"}
                                                                     onChange={(e) => {
                                                                         setFieldValue("allowCampaigns", e.target.checked ? "enabled" : "disabled");
                                                                     }}
                                                                     color="primary"
                                                                 />
                                                             }
                                                             label={
                                                                 <Box display="flex" alignItems="center">
                                                                     <Typography variant="body2">
                                                                         {i18n.t("userModal.form.allowCampaigns")}
                                                                     </Typography>
                                                                 </Box>
                                                             }
                                                         />
                                                     </Grid>
                                                 </Grid>

                                                 {/* Quinta linha - 2 permissões */}
                                                 <Grid container spacing={2}>
                                                     <Grid item xs={12} md={6} xl={6}>
                                                         <FormControlLabel
                                                             control={
                                                                 <Field
                                                                     as={Switch}
                                                                     name="allowContactSources"
                                                                     checked={values.allowContactSources === "enabled"}
                                                                     onChange={(e) => {
                                                                         setFieldValue("allowContactSources", e.target.checked ? "enabled" : "disabled");
                                                                     }}
                                                                     color="primary"
                                                                 />
                                                             }
                                                             label={
                                                                 <Box display="flex" alignItems="center">
                                                                     <Typography variant="body2">
                                                                         {i18n.t("userModal.form.allowContactSources")}
                                                                     </Typography>
                                                                 </Box>
                                                             }
                                                         />
                                                     </Grid>
                                                     <Grid item xs={12} md={6} xl={6}>
                                                         <FormControlLabel
                                                             control={
                                                                 <Field
                                                                     as={Switch}
                                                                     name="allowBulkTicketActions"
                                                                     checked={values.allowBulkTicketActions === "enabled"}
                                                                     onChange={(e) => {
                                                                         setFieldValue("allowBulkTicketActions", e.target.checked ? "enabled" : "disabled");
                                                                     }}
                                                                     color="primary"
                                                                 />
                                                             }
                                                             label={
                                                                 <Box display="flex" alignItems="center">
                                                                     <Typography variant="body2">
                                                                         Seleção múltipla de tickets
                                                                     </Typography>
                                                                     <Tooltip title="Ao habilitar a seleção múltipla de tickets, o controle financeiro das lanes será limitado.">
                                                                         <IconButton size="small" style={{ marginLeft: 8 }}>
                                                                             <InfoIcon fontSize="small" color="action" />
                                                                         </IconButton>
                                                                     </Tooltip>
                                                                 </Box>
                                                             }
                                                         />
                                                     </Grid>
                                                 </Grid>

                                                 {/* Sexta linha - 2 permissões */}
                                                 <Grid container spacing={2}>
                                                     <Grid item xs={12} md={6} xl={6}>
                                                         <FormControlLabel
                                                             control={
                                                                 <Field
                                                                     as={Switch}
                                                                     name="allowColumnManagement"
                                                                     checked={values.allowColumnManagement === "enabled"}
                                                                     onChange={(e) => {
                                                                         setFieldValue("allowColumnManagement", e.target.checked ? "enabled" : "disabled");
                                                                     }}
                                                                     color="primary"
                                                                 />
                                                             }
                                                             label={
                                                                 <Box display="flex" alignItems="center">
                                                                     <Typography variant="body2">
                                                                         Habilitar gerenciamento colunas
                                                                     </Typography>
                                                                     <Tooltip title="Usuário poderá criar, excluir e mover colunas no CRM.">
                                                                         <IconButton size="small" style={{ marginLeft: 8 }}>
                                                                             <InfoIcon fontSize="small" color="action" />
                                                                         </IconButton>
                                                                     </Tooltip>
                                                                 </Box>
                                                             }
                                                         />
                                                     </Grid>
                                                     <Grid item xs={12} md={6} xl={6}>
                                                         <FormControlLabel
                                                             control={
                                                                 <Field
                                                                     as={Switch}
                                                                     name="allowViewOtherConnections"
                                                                     checked={values.allowViewOtherConnections === "enabled"}
                                                                     onChange={(e) => {
                                                                         setFieldValue("allowViewOtherConnections", e.target.checked ? "enabled" : "disabled");
                                                                     }}
                                                                     color="primary"
                                                                 />
                                                             }
                                                             label={
                                                                 <Box display="flex" alignItems="center">
                                                                     <Typography variant="body2">
                                                                         {i18n.t("userModal.form.allowViewOtherConnections")}
                                                                     </Typography>
                                                                     <Tooltip title="Usuário poderá visualizar contatos de outras conexões.">
                                                                         <IconButton size="small" style={{ marginLeft: 8 }}>
                                                                             <InfoIcon fontSize="small" color="action" />
                                                                         </IconButton>
                                                                     </Tooltip>
                                                                 </Box>
                                                             }
                                                         />
                                                     </Grid>
                                                 </Grid>

                                                 {/* Sétima linha - 1 permissão */}
                                                 <Grid container spacing={2}>
                                                     <Grid item xs={12} md={6} xl={6}>
                                                         <FormControlLabel
                                                             control={
                                                                 <Field
                                                                     as={Switch}
                                                                     name="allowContactImportExport"
                                                                     checked={values.allowContactImportExport === "enabled"}
                                                                     onChange={(e) => {
                                                                         setFieldValue("allowContactImportExport", e.target.checked ? "enabled" : "disabled");
                                                                     }}
                                                                     color="primary"
                                                                 />
                                                             }
                                                             label={
                                                                 <Box display="flex" alignItems="center">
                                                                     <Typography variant="body2">
                                                                         {i18n.t("userModal.form.allowContactImportExport")}
                                                                     </Typography>
                                                                     <Tooltip title="Usuário poderá importar e exportar contatos nas TAGS.">
                                                                         <IconButton size="small" style={{ marginLeft: 8 }}>
                                                                             <InfoIcon fontSize="small" color="action" />
                                                                         </IconButton>
                                                                     </Tooltip>
                                                                 </Box>
                                                             }
                                                         />
                                                     </Grid>
                                                 </Grid>
                                             </>
                                         ) : (
                                            // Mensagem para usuários sem permissão
                                            <Typography variant="body1">
                                                Você não tem permissão.
                                            </Typography>
                                        )}
                                    </TabPanel>
                                </DialogContent>
                            </Paper>
                            <DialogActions>
                                <Button
                                    onClick={handleClose}
                                    color="secondary"
                                    disabled={isSubmitting}
                                    variant="outlined"
                                >
                                    {i18n.t("userModal.buttons.cancel")}
                                </Button>
                                <Button
                                    type="submit"
                                    color="primary"
                                    disabled={isSubmitting || !isValid || Object.keys(errors).length > 0}
                                    variant="contained"
                                    className={classes.btnWrapper}
                                >
                                    {userId
                                        ? `${i18n.t("userModal.buttons.okEdit")}`
                                        : `${i18n.t("userModal.buttons.okAdd")}`}
                                    {isSubmitting && (
                                        <CircularProgress
                                            size={24}
                                            className={classes.buttonProgress}
                                        />
                                    )}
                                </Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>
        </div >
    );
};

export default UserModal;

